import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Incident } from '@/api/client'
import { fmtTime, Banner, Empty, ErrorNote, Spinner, Tag, fmtDate } from '@/components/ui'
import { AwLogo } from '@/components/AwLogo'

/** What has gone wrong, and whether anybody was actually told.
 *
 *  The screen exists because the alert channel is inside the thing it monitors. A
 *  revoked bot token or a Telegram outage costs the push, not the record: every
 *  problem is a committed row before any message is attempted, so this page shows the
 *  day whether or not a single message got out. `delivered` is therefore the most
 *  important column here, not a detail - without it a broken bot and a quiet day look
 *  exactly the same.
 */

const WORD: Record<string, string> = {
  job_failed: 'A job failed',
  job_missed: 'A job was missed',
  silent_drop: 'Something was lost',
  delivery_failed: 'A message did not send',
  delivery_exhausted: 'A message was given up on',
  unreachable: 'Somebody received nothing',
  config_broken: 'A rule cannot fire',
  backup_failed: 'The backup failed',
  recovery_gap: 'The app was down',
  marker_anomaly: 'A job marker looks wrong',
}

function Row({ x }: { x: Incident }) {
  return (
    <tr className={x.state === 'acked' ? 'opacity-55' : ''}>
      <td className="td align-top whitespace-nowrap">
        <div className="text-[12.5px]">{fmtDate(x.last_seen)}</div>
        {/* Converted, not sliced: storage is UTC, so the slice showed 08:53 for an
            alert raised at 14:23. */}
        <div className="mono text-[11px] text-ink-3">{fmtTime(x.last_seen)}</div>
      </td>
      <td className="td align-top">
        <div className="flex items-center gap-2">
          {x.severity === 'immediate' ? <Tag tone="bad">Urgent</Tag> : null}
          <span className="text-[12px] text-ink-3">
            {WORD[x.category] ?? x.category}
          </span>
          {x.count > 1 ? (
            <span className="mono text-[11px] text-ink-3">×{x.count}</span>
          ) : null}
        </div>
        <div className="text-[13px] font-semibold mt-0.5">{x.summary}</div>
        {x.detail ? (
          <div className="text-[12.5px] text-ink-2 mt-0.5">{x.detail}</div>
        ) : null}
      </td>
      <td className="td align-top whitespace-nowrap text-[12px]">
        {x.delivered ? (
          <span className="text-ink-3">Sent</span>
        ) : x.notified_at === 'undeliverable' ? (
          <span className="text-rej-ink font-semibold">Never sent</span>
        ) : (
          <span className="text-pend-ink">Not sent yet</span>
        )}
      </td>
    </tr>
  )
}


type SeenPerson = {
  user_id: string; name: string; email: string; is_owner: boolean
  last_seen_at: string; seconds_ago: number | null; signed_in: boolean
}
type SignedIn = { now: string; within_seconds: number; people: SeenPerson[] }

/** "Ten minutes ago", and only as precisely as it is worth saying. */
function ago(seconds: number | null): string {
  if (seconds === null) return 'never'
  if (seconds < 60) return 'moments ago'
  const mins = Math.round(seconds / 60)
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

/** Who has the app open.
 *
 *  Sessions in this app are stateless - a JWT and a refresh cookie, revoked by
 *  bumping a global epoch - so there is no list of sessions to show and this cannot
 *  pretend there is. What it shows is when each person last made a request, which the
 *  API stamps on every authenticated call. The wording below says exactly that,
 *  because "signed in" would imply a precision the design does not have.
 */
function WhoIsHere() {
  const q = useQuery({
    queryKey: ['signed-in'],
    queryFn: () => api.get<SignedIn>('/ops/signed-in'),
    refetchInterval: 60_000,
  })
  if (q.isLoading) return <Spinner />
  if (q.error) return <ErrorNote error={q.error} />

  const people = q.data?.people ?? []
  const here = people.filter((p) => p.signed_in)
  // Somebody who has never been seen is not "idle for a long time", they are a person
  // this was switched on after. Kept apart so the two never read as the same thing.
  const seen = people.filter((p) => !p.signed_in && p.seconds_ago !== null)
    .sort((a, b) => (a.seconds_ago ?? 0) - (b.seconds_ago ?? 0))
  const never = people.filter((p) => p.seconds_ago === null)

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <b className="text-[13px]">Who has it open</b>
        <span className="text-[11.5px] text-ink-3">
          {here.length === 0
            ? 'Nobody right now'
            : `${here.length} of ${people.length} in the last few minutes`}
        </span>
      </div>
      <p className="text-[11.5px] text-ink-3 mt-1 mb-2">
        There is no list of sessions to read — signing in issues a token rather than
        opening one. This is when each person last used the app, and the app checks in
        about once a minute while it is open in front of somebody.
      </p>

      <div className="table-wrap">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className="th text-left">Name</th>
            <th className="th text-left w-44">Last used it</th>
            <th className="th text-left w-32">Open now?</th>
          </tr></thead>
          <tbody>
            {[...here, ...seen, ...never].map((p) => (
              <tr key={p.user_id}>
                <td className="td">
                  <b className="font-semibold text-[12.5px]">{p.name}</b>
                  {p.is_owner ? (
                    <span className="ml-1.5 align-middle"><Tag>owner</Tag></span>
                  ) : null}
                  <span className="block text-[11.5px] text-ink-3">{p.email}</span>
                </td>
                <td className="td text-[12.5px]">
                  {p.seconds_ago === null
                    ? <span className="text-ink-3">Not since this was added</span>
                    : ago(p.seconds_ago)}
                </td>
                <td className="td">
                  {p.signed_in
                    ? <span className="text-[12.5px] text-done-ink">● Open</span>
                    : <span className="text-[12.5px] text-ink-3">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function System() {
  const qc = useQueryClient()
  const incidents = useQuery({
    queryKey: ['incidents'],
    queryFn: () => api.get<Incident[]>('/ops/incidents'),
    refetchInterval: 60_000,
  })
  const ack = useMutation({
    mutationFn: () => api.post('/ops/incidents/ack', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incidents'] })
      qc.invalidateQueries({ queryKey: ['incident-count'] })
    },
  })

  if (incidents.isLoading) return <Spinner />
  if (incidents.error) return <ErrorNote error={incidents.error} />

  const rows = incidents.data ?? []
  const open = rows.filter((r) => r.state !== 'acked')
  const undelivered = open.filter((r) => !r.delivered && r.severity === 'immediate')

  return (
    <div className="space-y-4">
      {undelivered.length > 0 ? (
        <Banner tone="warn">
          {undelivered.length} urgent{' '}
          {undelivered.length === 1 ? 'problem has' : 'problems have'} not reached
          anyone on Telegram. They are recorded here regardless — if this keeps
          happening, check the bot token.
        </Banner>
      ) : null}

      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-ink-3">
          Everything the app noticed going wrong, newest first. Kept for 90 days.
        </p>
        {open.length > 0 ? (
          <button className="btn" onClick={() => ack.mutate()} disabled={ack.isPending}>
            {ack.isPending ? 'Clearing…' : `Mark all ${open.length} as read`}
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <Empty
          title="Nothing has gone wrong"
          hint="Failed jobs, undelivered messages and people who received nothing all appear here."
        />
      ) : (
        <div className="table-wrap">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="th text-left">When</th>
                <th className="th text-left">What happened</th>
                <th className="th text-left">Told you?</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <Row key={x.alert_id} x={x} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-4 mt-2 border-t border-rule">
        <WhoIsHere />
      </div>

      {/* The operator's screen, so it is also where the build identifies itself. The
          version is the one from package.json, substituted at build time - the same
          value the sidebar shows, so the two cannot disagree. */}
      <div className="flex items-center justify-between gap-4 pt-4 mt-2 border-t
                      border-rule text-[12px] text-ink-3">
        <div>
          <b className="text-ink-2 font-semibold">Task Manager</b>
          <span className="mono ml-2">v{__APP_VERSION__}</span>
        </div>
        <div className="text-center">
          <span className="block text-[10.5px] mb-1">Powered by</span>
          <AwLogo height={24} className="mx-auto" />
        </div>
      </div>
    </div>
  )
}
